import Vue from 'vue';
import axios from 'axios';
import { createNamespacedHelpers } from "vuex";
import { normalize } from "normalizr";
import merge from 'lodash/merge';

import { organizationChart, organizationChartItem } from "./schemas/organization-chart";

const state = {
  entities: null,
  roots: null,
  // tree: {},
  isLoading: false,
  isSaving: false,
  parent: null,
  itemsSelected: [],
  itemEditing: null,
  childSelected: null,
  state: 'none', // none, showing, creating, editing

  savingFailureMessage: 'No hemos podido guardar la información',
  savingFailureErrors: [],
}

const getters = {
  rootItems(state, getters) {
    if (state.roots === null) {
      return [];
    }

    if (state.parent === null) {
      return Object.keys(state.entities.organizationChart).map(key => state.entities.organizationChart[key]).filter(entity => entity.depth == 0);
      /* return state.roots.map(node => {
        return state.entities.organizationChart[node];
      }); */
    }
    
    return state.entities.organizationChart[state.parent].children.map(c => state.entities.organizationChart[c]);
  },
  allSelected(state) {
    return state.itemsSelected.map(item => {
      const { id, name, children } = state.entities.organizationChart[item]
      return { id, name, children };
    });
  },
  allSelectedLength(state, getters) {
    return getters.allSelected.length;
  },
  itemSelected(state) {
    const length = state.itemsSelected.length;

    if (length === 0) return null;

    return state.entities.organizationChart[state.itemsSelected[length - 1]];
  },
  payrollCategories(state, getters) {
    if (typeof state.entities.payrollCategory === 'undefined') return [];

    if (getters.itemsSelected === null) return [];

    return getters.itemSelected.payroll_categories.map(pc => state.entities.payrollCategory[pc]);
  },
  children(state, getters) {
    if (getters.itemSelected === null) return [];

    return getters.itemSelected.children.map(c => state.entities.organizationChart[c]);
  },
  jobTitles(state, getters) {
    if (typeof state.entities.jobTitle === 'undefined') return [];

    if (getters.itemsSelected === null) return [];

    return getters.itemsSelected.job_titles.map(jt => state.entities.jobTitle[jt]);
  },
  payrollPeriod(state, getters) {
    if (typeof state.entities.payrollPeriod === 'undefined') return {};

    if (getters.itemSelected === null) return {};
    
    return state.entities.payrollPeriod[getters.itemSelected.payroll_period]
  }
}

const mutations = {
  startLoad(state) {
    state.isLoading = true;
  },
  loadSuccess(state, payload) {
    state.entities = payload.entities;
    state.roots = payload.result;
    state.isLoading = false;
  },
  selectItem(state, { item, parent }) {
    state.state = 'showing';
    state.itemsSelected.push(item.id);
    state.parent = parent;
  },
  changeItem(state, item) {
    state.itemsSelected.splice(state.itemsSelected.length -1, 1, item.id);
  },
  back(state, index) {
    state.itemsSelected.splice(index + 1);
    state.parent = state.itemsSelected.length > 1 ? state.itemsSelected[state.itemsSelected.length - 2] : null; 
  },
  removeLastItemSelected(state) {
    if (state.itemsSelected.length > 0) {
      state.itemsSelected.pop();
    }
  },
  startCreate(state) {
    state.state = 'creating';
    
    state.itemEditing = {
      id: null,
      name: '',
      payroll_period: null,
      payroll_categories: []
    }
  },
  setParent(state, parent) {
    state.parent = parent;
  },
  updateItem(state, [field, value]) {
    Vue.set(state.itemEditing, field, value);
  },
  cancel(state) {
    state.state = state.itemEditing.id !== null ? 'showing' : 'none';
    state.itemEditing = null;
  },
  startEdit(state, item) {
    state.state = 'editing';

    state.itemEditing = {
      id: item.id,
      name: item.name,
      payroll_period: item.payroll_period,
      payroll_categories: item.payroll_categories
    };
  },
  startSaving(state) {
    state.isSaving =  true;
  },
  savingFailure(state, error) {
    
    if (error.response) {
      if (error.response.data.message) {
        state.savingFailureMessage = error.response.data.message;
      }  

      if (error.response.data.errors) {
        state.savingFailureErrors = Object.keys(error.response.data.errors).map(errorKey => {
          return error.response.data.errors[errorKey][0];
        });
      }
    }

    state.isSaving = false;
  },
  savingSuccess(state, data) {
    Vue.set(state, 'entities', merge(state.entities, data));
    state.isSaving = false;
  }
}

const actions = {
  async loadTree({ commit }) {
    commit('startLoad');

    const { data } = await axios.get('/api/organization-chart');

    commit('loadSuccess', normalize(data.data, organizationChart));
  },
  selectItem({ state, commit, getters }, item) {
    if (getters.itemSelected === null || item.depth > getters.itemSelected.depth) {
      commit('selectItem', { item, parent: (getters.itemSelected === null) ? null : getters.itemSelected.id });
    }  else if (item.depth === getters.itemSelected.depth) {
      commit('changeItem', item);
    }

    state.state = 'showing';
  },
  back({ state, commit, getters }, index) {
    commit('back', index);
  },
  startCreate({ commit, dispatch }) {
    // commit('removeLastItemSelected');
    commit('startCreate');
    dispatch('payrollTypes/loadAll', null, { root: true });
    dispatch('payrollCategories/loadAll', null, { root: true });
  },
  startCreateClasificationFromItemSelected({ commit, dispatch, getters }) {
    commit('setParent', getters.itemSelected.id);
    dispatch('startCreate');
  },
  startEdit({ getters, commit, dispatch }) {
    commit('startEdit', getters.itemSelected);
    dispatch('payrollTypes/loadAll', null, { root: true });
    dispatch('payrollCategories/loadAll', null, { root: true });
  },
  async save({ state, commit }) {
    commit('startSaving');
    let method = 'post';
    let url = '/api/organization-chart-items';

    if (state.itemEditing.id !== null) {
      method = 'put';
      url += `/${state.itemEditing.id}`;
    }

    try {
      let { data } = await axios({
        method,
        url,
        data: { ...state.itemEditing, parent: state.parent },
      });

      commit('savingSuccess', normalize([data.data], organizationChart).entities);
    } catch (error) {
      commit('savingFailure', error);
      throw error;
    }
  }
}

export const {
  mapState: mapOrganizationChartState,
  mapGetters: mapOrganizationChartGetters,
  mapMutations: mapOrganizationChartMutations,
  mapActions: mapOrganizationChartActions
} = createNamespacedHelpers('organizationChart');

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
